/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Port utility functions for detecting and finding free ports
 */
import { execaCommand } from 'execa';

/**
 * Check if a port is in use
 */
export const isPortInUse = async (port: number): Promise<boolean> => {
  try {
    // Use lsof on Unix-like systems
    if (process.platform !== 'win32') {
      await execaCommand(`lsof -i :${port}`, { shell: true });
      return true;
    } else {
      // Use netstat on Windows
      const result = await execaCommand(`netstat -ano | findstr :${port}`, { shell: true });
      return result.stdout.length > 0;
    }
  } catch {
    return false;
  }
};

/**
 * Find the next available port starting from a given port
 */
export const findFreePort = async (startPort: number): Promise<number> => {
  let port = startPort;
  const maxAttempts = 10;

  for (let i = 0; i < maxAttempts; i++) {
    const inUse = await isPortInUse(port);
    if (!inUse) {
      return port;
    }
    port++;
  }

  return startPort; // Fallback to original port
};

/**
 * Common default ports for different frameworks
 */
export const DEFAULT_PORTS = {
  vite: 5173,
  nextjs: 3000,
  cra: 3000,
  angular: 4200,
  express: 3000,
  fastify: 3000,
  streamlit: 8501,
  fastapi: 8000,
  flask: 5000,
  flutter: 8080,
} as const;

